export const PEOPLE_CATEGORY_ANSWERS = "Answers & Assistance";
export const PEOPLE_CATEGORY_COMMUNICATION = "Communication & Engagement";
export const PEOPLE_CATEGORY_ROUTING = "Team Connections & Routing";
export const PEOPLE_CATEGORY_CONVERSION = "Lead Capture & Conversion";

export type PeopleCategoryId =
  | "people.answers"
  | "people.communication"
  | "people.routing"
  | "people.conversion";

export type PeopleCheckId =
  | "P-A01"
  | "P-A02"
  | "P-A03"
  | "P-A04"
  | "P-A05"
  | "P-A06"
  | "P-C01"
  | "P-C02"
  | "P-C03"
  | "P-C04"
  | "P-C05"
  | "P-C06"
  | "P-R01"
  | "P-R02"
  | "P-R03"
  | "P-R04"
  | "P-R05"
  | "P-R06"
  | "P-L01"
  | "P-L02"
  | "P-L03"
  | "P-L04"
  | "P-L05"
  | "P-L06";

export type PeopleDetectionTier = "passive" | "interactive" | "manual";

export type PeopleCheckDefinition = {
  id: PeopleCheckId;
  categoryId: PeopleCategoryId;
  category: string;
  title: string;
  question: string;
  goal: string;
  impact: string;
  recommendedFix: string;
  capabilityIds: string[];
  weight: number;
  detectionTier: PeopleDetectionTier;
  /** When true, Phase 1 never treats presence as a pass. */
  phase1Unknown: boolean;
};

export const PEOPLE_CATEGORIES: {
  id: PeopleCategoryId;
  label: string;
  question: string;
  description: string;
}[] = [
  {
    id: "people.answers",
    label: PEOPLE_CATEGORY_ANSWERS,
    question: "Can visitors get accurate answers and helpful assistance immediately?",
    description:
      "Customers can get helpful, accurate answers about your business, services, and locations without waiting for staff.",
  },
  {
    id: "people.communication",
    label: PEOPLE_CATEGORY_COMMUNICATION,
    question: "Can customers reach your business through their preferred communication channel?",
    description:
      "Customers can start and continue conversations using the channels they prefer, including outside normal business hours.",
  },
  {
    id: "people.routing",
    label: PEOPLE_CATEGORY_ROUTING,
    question: "Can every customer connect with the right person, department, or location?",
    description:
      "Customers are directed to the right location, department, or team member without repeating information or navigating multiple menus.",
  },
  {
    id: "people.conversion",
    label: PEOPLE_CATEGORY_CONVERSION,
    question: "Can your website turn conversations into qualified leads, appointments, and customers?",
    description:
      "Every visitor has a clear path to ask for help, request an appointment, or become a qualified lead.",
  },
];

export const PEOPLE_CHECKS: PeopleCheckDefinition[] = [
  {
    id: "P-A01",
    categoryId: "people.answers",
    category: PEOPLE_CATEGORY_ANSWERS,
    title: "Clear business and service information",
    question: "Can visitors tell what you do and what you offer?",
    goal: "Headlines, service descriptions, and trust signals should make the business obvious.",
    impact: "People leave when they cannot tell what the business does.",
    recommendedFix: "Publish a clear headline, service list, and a short proof point the business already uses.",
    capabilityIds: ["rag_knowledge"],
    weight: 1,
    detectionTier: "passive",
    phase1Unknown: false,
  },
  {
    id: "P-A02",
    categoryId: "people.answers",
    category: PEOPLE_CATEGORY_ANSWERS,
    title: "Frequently asked questions answered",
    question: "Can visitors get common questions answered without waiting for staff?",
    goal: "FAQ and policy answers should be readable on the site.",
    impact: "Missing answers send people to the phone or away from the site.",
    recommendedFix: "Publish the questions customers already ask, then let SiteGlue answer them in chat.",
    capabilityIds: ["ai_support", "rag_knowledge"],
    weight: 1,
    detectionTier: "passive",
    phase1Unknown: false,
  },
  {
    id: "P-A03",
    categoryId: "people.answers",
    category: PEOPLE_CATEGORY_ANSWERS,
    title: "Service and location-specific answers",
    question: "Can visitors get answers that match a service and a location?",
    goal: "Services should be associated with the places that offer them.",
    impact: "Generic copy leaves visitors unsure which location can help them.",
    recommendedFix: "Add location pages or service-and-address pairings the team can retrieve.",
    capabilityIds: ["rag_knowledge", "company_directory"],
    weight: 1,
    detectionTier: "passive",
    phase1Unknown: false,
  },
  {
    id: "P-A04",
    categoryId: "people.answers",
    category: PEOPLE_CATEGORY_ANSWERS,
    title: "Interactive assistance available",
    question: "Can visitors start a conversation and get help on the page?",
    goal: "A help entry point should be visible. Answer quality is not scored from HTML.",
    impact: "No help control means questions wait for a form or a phone call.",
    recommendedFix: "Add website chat so visitors can ask without leaving the page.",
    capabilityIds: ["website_chat"],
    weight: 1,
    detectionTier: "passive",
    phase1Unknown: false,
  },
  {
    id: "P-A05",
    categoryId: "people.answers",
    category: PEOPLE_CATEGORY_ANSWERS,
    title: "Accurate answers from business knowledge",
    question: "Do answers come from this business’s own facts, not a generic guess?",
    goal: "Public claims should match if a visitor or agent asks.",
    impact: "Conflicting or generic answers undermine trust. HTML cannot prove retrieval quality.",
    recommendedFix: "Publish the facts you want retrieved, then test the help path with consent.",
    capabilityIds: ["rag_knowledge"],
    weight: 1,
    detectionTier: "interactive",
    phase1Unknown: true,
  },
  {
    id: "P-A06",
    categoryId: "people.answers",
    category: PEOPLE_CATEGORY_ANSWERS,
    title: "Assistance beyond business hours",
    question: "Can someone get help when the office is closed?",
    goal: "After-hours help should be an explicit promise or a verified test.",
    impact: "A chat bubble is not proof that someone answers at night.",
    recommendedFix: "Cover chat and phone after hours with an AI employee that knows local time.",
    capabilityIds: ["after_hours", "local_time"],
    weight: 1,
    detectionTier: "passive",
    phase1Unknown: false,
  },
  {
    id: "P-C01",
    categoryId: "people.communication",
    category: PEOPLE_CATEGORY_COMMUNICATION,
    title: "Website live chat",
    question: "Can visitors start a live chat on the website?",
    goal: "A chat control should be present. A reply is not tested in a passive scan.",
    impact: "No chat control leaves only forms and phone.",
    recommendedFix: "Add AI-powered website chat visitors can open. Third-party tools count.",
    capabilityIds: ["website_chat"],
    weight: 1,
    detectionTier: "passive",
    phase1Unknown: false,
  },
  {
    id: "P-C02",
    categoryId: "people.communication",
    category: PEOPLE_CATEGORY_COMMUNICATION,
    title: "Click-to-call and browser calling",
    question: "Can visitors call without hunting for a number, including from the browser?",
    goal: "A telephone link counts. Browser calling is optional unless the page advertises it.",
    impact: "Visitors may leave the website to call your business.",
    recommendedFix: "Publish click-to-call, and add browser calling with team transfer when needed.",
    capabilityIds: ["phone", "browser_call"],
    weight: 1,
    detectionTier: "passive",
    phase1Unknown: false,
  },
  {
    id: "P-C03",
    categoryId: "people.communication",
    category: PEOPLE_CATEGORY_COMMUNICATION,
    title: "SMS communication",
    question: "Can visitors text the business if that channel fits how they reach you?",
    goal: "SMS is optional unless the page tells people to text.",
    impact: "Missing SMS is not a failure unless the site advertises texting.",
    recommendedFix: "Add an SMS path if customers already text you.",
    capabilityIds: ["sms"],
    weight: 1,
    detectionTier: "passive",
    phase1Unknown: false,
  },
  {
    id: "P-C04",
    categoryId: "people.communication",
    category: PEOPLE_CATEGORY_COMMUNICATION,
    title: "Email communication",
    question: "Can visitors email the business and continue the conversation later?",
    goal: "A mailto link, address, or form should be available.",
    impact: "No email path leaves only phone or chat.",
    recommendedFix: "Publish an email path, or let SiteGlue handle email conversations.",
    capabilityIds: ["email"],
    weight: 1,
    detectionTier: "passive",
    phase1Unknown: false,
  },
  {
    id: "P-C05",
    categoryId: "people.communication",
    category: PEOPLE_CATEGORY_COMMUNICATION,
    title: "Video conversations",
    question: "Can visitors start a video conversation when that channel fits the business?",
    goal: "Video is optional for most businesses and must not be a default miss.",
    impact: "Missing video should not fail a clinic or store that does not offer video visits.",
    recommendedFix: "Add video calls only if customers already meet you that way.",
    capabilityIds: ["video"],
    weight: 1,
    detectionTier: "passive",
    phase1Unknown: false,
  },
  {
    id: "P-C06",
    categoryId: "people.communication",
    category: PEOPLE_CATEGORY_COMMUNICATION,
    title: "After-hours and personalized greetings",
    question: "Does the site greet people for the time of day and say when someone is available?",
    goal: "Published hours are scored. Time-of-day greetings need a live test.",
    impact: "Visitors cannot plan a visit without hours, and generic greetings feel closed.",
    recommendedFix: "Publish hours and greet visitors by local time, including after hours.",
    capabilityIds: ["local_time", "greetings"],
    weight: 1,
    detectionTier: "passive",
    phase1Unknown: false,
  },
  {
    id: "P-R01",
    categoryId: "people.routing",
    category: PEOPLE_CATEGORY_ROUTING,
    title: "Business locations clearly identified",
    question: "Can visitors tell where the business operates?",
    goal: "An address or location list should be public unless the business is locationless.",
    impact: "People cannot choose a place to visit or call.",
    recommendedFix: "Publish the locations customers should choose from.",
    capabilityIds: ["company_directory"],
    weight: 1,
    detectionTier: "passive",
    phase1Unknown: false,
  },
  {
    id: "P-R02",
    categoryId: "people.routing",
    category: PEOPLE_CATEGORY_ROUTING,
    title: "Location-specific contact information",
    question: "Can visitors reach the location that can actually help them?",
    goal: "Multi-location sites should offer a phone, form, or routing note per place.",
    impact: "One shared number leaves visitors guessing which office they reached.",
    recommendedFix: "Give each location its own contact path in the company directory.",
    capabilityIds: ["company_directory"],
    weight: 1,
    detectionTier: "passive",
    phase1Unknown: false,
  },
  {
    id: "P-R03",
    categoryId: "people.routing",
    category: PEOPLE_CATEGORY_ROUTING,
    title: "Department and team contact information",
    question: "Can visitors reach the right department or team without repeating themselves?",
    goal: "A directory or labeled service contact should exist.",
    impact: "Team names without a path do not help a visitor connect.",
    recommendedFix: "Publish department contacts, then route conversations to that team.",
    capabilityIds: ["company_directory", "team_handoff"],
    weight: 1,
    detectionTier: "passive",
    phase1Unknown: false,
  },
  {
    id: "P-R04",
    categoryId: "people.routing",
    category: PEOPLE_CATEGORY_ROUTING,
    title: "Business hours and availability",
    question: "Can visitors tell when someone is available, including by location?",
    goal: "Hours should be structured or clearly listed.",
    impact: "Missing hours send people to guess or call when you are closed.",
    recommendedFix: "Publish hours so greetings and routing can use local time.",
    capabilityIds: ["local_time"],
    weight: 1,
    detectionTier: "passive",
    phase1Unknown: false,
  },
  {
    id: "P-R05",
    categoryId: "people.routing",
    category: PEOPLE_CATEGORY_ROUTING,
    title: "Ability to reach a real person",
    question: "Can a visitor reach a real person when the conversation needs one?",
    goal: "A contact or transfer path should be visible. A successful handoff is not tested.",
    impact: "Visitors cannot escalate when the page never offers a person.",
    recommendedFix: "Show how to reach a teammate, or enable Team Handoff.",
    capabilityIds: ["team_handoff"],
    weight: 1,
    detectionTier: "passive",
    phase1Unknown: false,
  },
  {
    id: "P-R06",
    categoryId: "people.routing",
    category: PEOPLE_CATEGORY_ROUTING,
    title: "Intelligent routing and escalation",
    question: "Does the conversation go to the right person without extra menus?",
    goal: "Routing quality needs an authorized test or owner confirmation.",
    impact: "Passive HTML cannot prove intent routing.",
    recommendedFix: "Use intent recognition and handoff actions after you choose departments.",
    capabilityIds: ["intent_routing", "team_handoff"],
    weight: 1,
    detectionTier: "interactive",
    phase1Unknown: true,
  },
  {
    id: "P-L01",
    categoryId: "people.conversion",
    category: PEOPLE_CATEGORY_CONVERSION,
    title: "Clear calls to action",
    question: "Is the next step obvious — call, book, ask, or get help?",
    goal: "A direct action should lead to a real destination.",
    impact: "Vague CTAs stop inquiries before they start.",
    recommendedFix: "Use a direct action and point it at chat, phone, a form, or booking.",
    capabilityIds: ["website_cro"],
    weight: 1,
    detectionTier: "passive",
    phase1Unknown: false,
  },
  {
    id: "P-L02",
    categoryId: "people.conversion",
    category: PEOPLE_CATEGORY_CONVERSION,
    title: "Contact and inquiry capture",
    question: "Can a visitor ask for help or leave an inquiry without getting stuck?",
    goal: "A form or conversational intake control should exist. The scan never submits it.",
    impact: "No capture path means the visit ends without a lead.",
    recommendedFix: "Add conversational lead capture so visitors can ask without a long form.",
    capabilityIds: ["lead_capture", "ai_intake"],
    weight: 1,
    detectionTier: "passive",
    phase1Unknown: false,
  },
  {
    id: "P-L03",
    categoryId: "people.conversion",
    category: PEOPLE_CATEGORY_CONVERSION,
    title: "Lead qualification",
    question: "Does the conversation ask the right questions before handing off a lead?",
    goal: "Qualification is not inferred from a form existing.",
    impact: "A name-and-email form is not proof of qualification.",
    recommendedFix: "Add AI-powered intake questions that match how you qualify leads.",
    capabilityIds: ["ai_intake"],
    weight: 1,
    detectionTier: "interactive",
    phase1Unknown: true,
  },
  {
    id: "P-L04",
    categoryId: "people.conversion",
    category: PEOPLE_CATEGORY_CONVERSION,
    title: "Appointment requests and booking",
    question: "Can a visitor request or book an appointment when that is how you work?",
    goal: "A booking or request path should exist when appointments apply.",
    impact: "No booking path stalls appointment businesses. Stores that do not book are not failed.",
    recommendedFix: "Connect the calendar or EMR you already use, or take appointment requests.",
    capabilityIds: ["scheduling"],
    weight: 1,
    detectionTier: "passive",
    phase1Unknown: false,
  },
  {
    id: "P-L05",
    categoryId: "people.conversion",
    category: PEOPLE_CATEGORY_CONVERSION,
    title: "Follow-up and callback requests",
    question: "Can a visitor ask someone to follow up by text, email, or a call?",
    goal: "A callback or follow-up path should be visible. Automation is not proven.",
    impact: "No follow-up path leaves inquiries hanging.",
    recommendedFix: "Offer callback, SMS, or email follow-up, and hand off to the team when needed.",
    capabilityIds: ["follow_up", "sms", "email", "team_handoff"],
    weight: 1,
    detectionTier: "passive",
    phase1Unknown: false,
  },
  {
    id: "P-L06",
    categoryId: "people.conversion",
    category: PEOPLE_CATEGORY_CONVERSION,
    title: "Conversion and inquiry tracking",
    question: "Can the team see which conversations became inquiries?",
    goal: "Public analytics tags do not prove reporting works.",
    impact: "HTML tags alone are not conversion reporting.",
    recommendedFix: "Review AI activity and reporting after conversations run, not public GTM tags.",
    capabilityIds: ["reporting"],
    weight: 1,
    detectionTier: "manual",
    phase1Unknown: true,
  },
];

export function peopleCheckById(id: string): PeopleCheckDefinition | undefined {
  return PEOPLE_CHECKS.find((check) => check.id === id);
}

export function peopleChecksForCategory(categoryId: PeopleCategoryId): PeopleCheckDefinition[] {
  return PEOPLE_CHECKS.filter((check) => check.categoryId === categoryId);
}

export function isPeopleCheckId(id: string): id is PeopleCheckId {
  return PEOPLE_CHECKS.some((check) => check.id === id);
}
